import {
  ProtocolValidationError,
  requireByteLength,
  requireSequence,
  requireUuid,
} from "./validation";

export const MEDIA_FRAME_VERSION = 2 as const;
export const MEDIA_FRAME_TYPE_CHUNK = 1 as const;
export const MEDIA_FRAME_FINAL_FLAG = 0x01;
export const MEDIA_FRAME_HEADER_SIZE = 36;
export const MEDIA_FRAME_MAX_PAYLOAD_SIZE = 64 * 1024;

const MEDIA_FRAME_MAGIC_BYTES = [0x41, 0x53, 0x4d, 0x32] as const;
export const MEDIA_FRAME_MAGIC = new Uint8Array(MEDIA_FRAME_MAGIC_BYTES);

export interface MediaFrame {
  readonly version: typeof MEDIA_FRAME_VERSION;
  readonly requestId: string;
  readonly sequence: number;
  readonly final: boolean;
  readonly totalByteLength: number;
  readonly payload: Uint8Array;
}

export interface MediaFrameInput {
  readonly requestId: string;
  readonly sequence: number;
  readonly final: boolean;
  readonly totalByteLength: number;
  readonly payload: Uint8Array;
}

export function encodeMediaFrame(frame: MediaFrameInput): Uint8Array {
  const requestId = requireUuid(frame.requestId, "$.requestId");
  const sequence = requireSequence(frame.sequence, "$.sequence");
  const totalByteLength = requireByteLength(
    frame.totalByteLength,
    "$.totalByteLength",
  );
  const payload = requirePayload(frame.payload);
  if (payload.byteLength > totalByteLength) {
    throw frameError(
      "$.payload",
      "payload cannot exceed the declared total byte length",
    );
  }
  const bytes = new Uint8Array(
    MEDIA_FRAME_HEADER_SIZE + payload.byteLength,
  );
  bytes.set(MEDIA_FRAME_MAGIC_BYTES, 0);
  bytes[4] = MEDIA_FRAME_VERSION;
  bytes[5] = MEDIA_FRAME_TYPE_CHUNK;
  bytes[6] = frame.final ? MEDIA_FRAME_FINAL_FLAG : 0;
  bytes[7] = MEDIA_FRAME_HEADER_SIZE;
  bytes.set(uuidToBytes(requestId), 8);
  const view = new DataView(bytes.buffer);
  view.setUint32(24, sequence, false);
  view.setUint32(28, payload.byteLength, false);
  view.setUint32(32, totalByteLength, false);
  bytes.set(payload, MEDIA_FRAME_HEADER_SIZE);
  return bytes;
}

export function decodeMediaFrame(
  input: ArrayBuffer | ArrayBufferView,
): MediaFrame {
  const bytes = toUint8Array(input);
  if (bytes.byteLength < MEDIA_FRAME_HEADER_SIZE) {
    throw frameError("$", "frame header is truncated");
  }
  for (let index = 0; index < MEDIA_FRAME_MAGIC_BYTES.length; index += 1) {
    if (bytes[index] !== MEDIA_FRAME_MAGIC_BYTES[index]) {
      throw frameError("$", "frame magic is invalid");
    }
  }
  if (bytes[4] !== MEDIA_FRAME_VERSION) {
    throw new ProtocolValidationError(
      "unsupported_version",
      `Unsupported media frame version: ${String(bytes[4])}`,
      "$.version",
    );
  }
  if (bytes[5] !== MEDIA_FRAME_TYPE_CHUNK) {
    throw frameError("$.type", "frame type is unsupported");
  }
  if ((bytes[6]! & ~MEDIA_FRAME_FINAL_FLAG) !== 0) {
    throw frameError("$.flags", "frame contains unknown flags");
  }
  if (bytes[7] !== MEDIA_FRAME_HEADER_SIZE) {
    throw frameError("$.headerSize", "frame header size is invalid");
  }
  const view = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength,
  );
  const sequence = view.getUint32(24, false);
  const payloadLength = view.getUint32(28, false);
  const totalByteLength = view.getUint32(32, false);
  if (payloadLength > MEDIA_FRAME_MAX_PAYLOAD_SIZE) {
    throw frameError("$.payloadLength", "frame payload exceeds 64 KiB");
  }
  if (payloadLength > totalByteLength) {
    throw frameError(
      "$.payloadLength",
      "frame payload exceeds its declared total byte length",
    );
  }
  if (bytes.byteLength !== MEDIA_FRAME_HEADER_SIZE + payloadLength) {
    throw frameError(
      "$.payloadLength",
      "declared payload length does not match the frame",
    );
  }
  const requestId = bytesToUuid(bytes.subarray(8, 24));
  requireUuid(requestId, "$.requestId");
  return {
    version: MEDIA_FRAME_VERSION,
    requestId,
    sequence,
    final: (bytes[6]! & MEDIA_FRAME_FINAL_FLAG) !== 0,
    totalByteLength,
    payload: bytes.slice(MEDIA_FRAME_HEADER_SIZE),
  };
}

function requirePayload(value: unknown): Uint8Array {
  if (!(value instanceof Uint8Array)) {
    throw frameError("$.payload", "payload must be a Uint8Array");
  }
  if (value.byteLength > MEDIA_FRAME_MAX_PAYLOAD_SIZE) {
    throw frameError("$.payload", "payload exceeds 64 KiB");
  }
  return value;
}

function toUint8Array(input: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }
  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(
      input.buffer,
      input.byteOffset,
      input.byteLength,
    );
  }
  throw frameError("$", "frame must be an ArrayBuffer or ArrayBuffer view");
}

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replaceAll("-", "");
  const bytes = new Uint8Array(16);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(
    bytes,
    (byte) => byte.toString(16).padStart(2, "0"),
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

function frameError(path: string, detail: string): ProtocolValidationError {
  return new ProtocolValidationError(
    "malformed",
    `${path} ${detail}`,
    path,
  );
}
